import { useMemo, useState } from 'react';

type Point = {
  month: string;
  value: number;
  secondary?: number;
};

function range(values: readonly number[]) {
  const max = Math.max(...values);
  const min = Math.min(...values);
  return { min, max, span: Math.max(1, max - min) };
}

export function Sparkline({ values }: { values: readonly number[] }) {
  const { min, span } = range(values);
  const points = values.map((value, index) => {
    const x = (index / Math.max(1, values.length - 1)) * 100;
    const y = 34 - ((value - min) / span) * 28;
    return x + ',' + y;
  }).join(' ');

  return (
    <svg className="sparkline" viewBox="0 0 100 36" preserveAspectRatio="none" aria-hidden="true">
      <polyline points={points} fill="none" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export function BarChart({ data }: { data: readonly Point[] }) {
  const [active, setActive] = useState<number | null>(data.length - 1);
  const max = Math.max(...data.map((item) => item.value));

  return (
    <div className="bar-chart-shell">
      <div className="bar-chart" aria-label="Revenue by month">
        {data.map((item, index) => {
          const selected = active === index;
          return (
            <button
              className={selected ? 'bar-col active' : 'bar-col'}
              key={item.month}
              onMouseEnter={() => setActive(index)}
              onFocus={() => setActive(index)}
              onClick={() => setActive(index)}
              aria-label={item.month + ': ' + item.value}
            >
              <span className="chart-tooltip">{item.month} · {'$'}{item.value}K</span>
              <div className="bar-wrap"><span style={{ height: String((item.value / max) * 100) + '%' }} /></div>
              <small>{item.month}</small>
            </button>
          );
        })}
      </div>
      {active !== null ? (
        <div className="chart-readout">
          <span>{data[active]?.month}</span>
          <strong>{'$'}{data[active]?.value}K</strong>
          <small>recognized revenue</small>
        </div>
      ) : null}
    </div>
  );
}

export function AreaChart({ data }: { data: readonly Point[] }) {
  const [active, setActive] = useState(data.length - 1);
  const values = useMemo(
    () => data.flatMap((item) => [item.value, item.secondary ?? item.value]),
    [data],
  );
  const { min, span } = range(values);

  const toPoint = (value: number, index: number) => {
    const x = 6 + (index / Math.max(1, data.length - 1)) * 88;
    const y = 80 - ((value - min) / span) * 60;
    return { x, y };
  };

  const primary = data.map((item, index) => toPoint(item.value, index));
  const secondary = data.map((item, index) => toPoint(item.secondary ?? item.value, index));
  const primaryPath = primary.map((point) => point.x + ',' + point.y).join(' ');
  const secondaryPath = secondary.map((point) => point.x + ',' + point.y).join(' ');
  const areaPath =
    'M ' + primary[0]?.x + ' 88 L ' +
    primary.map((point) => point.x + ' ' + point.y).join(' L ') +
    ' L ' + primary.at(-1)?.x + ' 88 Z';

  return (
    <div className="area-chart-shell">
      <svg className="area-chart" viewBox="0 0 100 92" role="img" aria-label="Revenue growth chart">
        <defs>
          <linearGradient id="area-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.24" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#area-fill)" />
        <polyline className="area-line secondary" points={secondaryPath} fill="none" />
        <polyline className="area-line primary" points={primaryPath} fill="none" />
        {primary.map((point, index) => (
          <g key={data[index]?.month}>
            <circle
              className={active === index ? 'area-point active' : 'area-point'}
              cx={point.x}
              cy={point.y}
              r={active === index ? 2.4 : 1.35}
            />
            <rect
              x={point.x - 5}
              y="0"
              width="10"
              height="92"
              fill="transparent"
              tabIndex={0}
              onMouseEnter={() => setActive(index)}
              onFocus={() => setActive(index)}
              onClick={() => setActive(index)}
              aria-label={data[index]?.month + ': ' + data[index]?.value}
            />
          </g>
        ))}
      </svg>
      <div className="area-chart-axis">{data.map((item) => <span key={item.month}>{item.month}</span>)}</div>
      <div className="area-chart-readout">
        <span>{data[active]?.month}</span>
        <strong>{'$'}{data[active]?.value}K</strong>
        <small>
          {'Primary · comparison ' + (data[active]?.secondary ? '$' + data[active]?.secondary + 'K' : '—')}
        </small>
      </div>
    </div>
  );
}

export function Donut({ value, label, sublabel }: { value: number; label: string; sublabel: string }) {
  return (
    <div className="donut-wrap">
      <div
        className="donut"
        role="img"
        aria-label={label + ': ' + value + '%'}
        style={{ background: 'conic-gradient(var(--accent) ' + String(value * 3.6) + 'deg, var(--surface-3) 0)' }}
      >
        <div><strong>{value}%</strong><span>{label}</span></div>
      </div>
      <small>{sublabel}</small>
    </div>
  );
}
